import { getLocalTimeZone, now } from '@internationalized/date';
import {
	Button,
	ButtonGroup,
	DatePicker,
	DialogTrigger,
	Heading,
	Popover,
	Text,
} from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import {
	type FormEvent,
	type PropsWithChildren,
	type ReactNode,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	parseStoredTriggerTime,
	scheduleDraftError,
} from '@/spaces/chat/panels/scheduleUtils';

interface SchedulePopoverProps {
	isOpen: boolean;
	scheduleDraft: string;
	onScheduleDraftChange: (value: string) => void;
	onCancel: () => void;
	onConfirm: () => void | Promise<void>;
	heading?: string;
	confirmLabel?: string;
	alternateBranch?: ReactNode;
	// True when the backend behind this popover judges the picked time on the
	// whole minute rather than the instant. An email send is judged that way, an
	// Express post is not. See `scheduleDraftError`.
	heldOnWholeMinute?: boolean;
}

export const SchedulePopover = ({
	children,
	isOpen,
	scheduleDraft,
	onScheduleDraftChange,
	onCancel,
	onConfirm,
	heading = 'Schedule',
	confirmLabel = 'Schedule',
	alternateBranch,
	heldOnWholeMinute = false,
}: PropsWithChildren<SchedulePopoverProps>) => {
	const tz = getLocalTimeZone();
	const fallbackDateRef = useRef(now(tz));
	const [scheduleError, setScheduleError] = useState<string | null>(null);

	const datePickerDefaultValue = useMemo(() => {
		if (!scheduleDraft) return fallbackDateRef.current;
		// A stored draft that names no zone is read as UTC, the way the server reads
		// it. `parseAbsoluteToLocal` throws on that shape and the picker then opened
		// on "now" instead of the time the customer had already chosen.
		return parseStoredTriggerTime(scheduleDraft) ?? fallbackDateRef.current;
	}, [scheduleDraft]);

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		const error = scheduleDraftError(scheduleDraft, { heldOnWholeMinute });
		if (error) {
			setScheduleError(error);
			return;
		}
		setScheduleError(null);
		void onConfirm();
	};

	return (
		<DialogTrigger>
			{children}
			<Popover
				placement="bottom"
				hideArrow={true}
				isOpen={isOpen}
				onOpenChange={(nextOpen) => {
					if (!nextOpen) {
						setScheduleError(null);
						onCancel();
					}
				}}
			>
				{alternateBranch ?? (
					<form
						className="flex min-w-[360px] flex-col gap-6 p-6"
						onSubmit={handleSubmit}
					>
						<Heading
							styles={style({
								font: 'title',
								color: 'heading',
								fontSize: 'title-lg',
								fontWeight: 'bold',
							})}
						>
							{heading}
						</Heading>

						<div className="flex min-w-0 flex-1 flex-col gap-2">
							<DatePicker
								granularity="minute"
								defaultValue={datePickerDefaultValue}
								minValue={now(tz)}
								onChange={(value) => {
									setScheduleError(null);
									if (!value) return;
									onScheduleDraftChange(value.toDate().toISOString());
								}}
							/>
						</div>

						{scheduleError && (
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'negative',
								})}
							>
								{scheduleError}
							</Text>
						)}

						<ButtonGroup UNSAFE_className="justify-center pt-2">
							<Button variant="secondary" type="button" onPress={onCancel}>
								Cancel
							</Button>
							<Button variant="primary" type="submit">
								{confirmLabel}
							</Button>
						</ButtonGroup>
					</form>
				)}
			</Popover>
		</DialogTrigger>
	);
};
